import * as React from "react";
import { ThemeProvider } from "styled-components";
import { getParentUrlFromPath } from "utils/path";

import { HeaderBar } from "components/HeaderBar";
import { useIsAtTopOfPage } from "hooks/useIsAtTopOfPage";

import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import {
  AppContainer,
  AppWrapper,
  ContentContainer,
  FooterWrapper,
  LayoutNavWrapper,
  PageHeader,
} from "../sections/layout.styles";
import { GlobalStyles, Section, theme } from "../styles";
import { MenuProvider } from "contexts/menu";

type LayoutProps = {
  children: React.ReactNode;
  title?: string;
  pathname?: string;
  hasBackgroundImage?: boolean;
  variant?: React.ComponentProps<typeof AppContainer>["$variant"];
};

const Layout = ({
  children,
  hasBackgroundImage,
  pathname,
  title,
  variant = "white",
}: LayoutProps) => {
  const [hasLoadedLocalStorage, setHasLoadedLocalStorage] =
    React.useState(false);
  const [showHeaderBar, setShowHeaderBar] = React.useState(true);

  React.useEffect(() => {
    if (localStorage.getItem("hideHeaderBar")) {
      setShowHeaderBar(false);
    }
    setHasLoadedLocalStorage(true);
  }, []);

  const handleHeaderBarClose = () => {
    localStorage.setItem("hideHeaderBar", "true");
    setShowHeaderBar(false);
  };

  const parentUrl = getParentUrlFromPath(pathname);

  const isAtTopOfPage = useIsAtTopOfPage();

  if (!hasLoadedLocalStorage) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <MenuProvider>
        <AppWrapper>
          <GlobalStyles />
          {hasLoadedLocalStorage && showHeaderBar && (
            <HeaderBar onClose={handleHeaderBarClose} />
          )}
          <AppContainer $variant={variant} $isAtTop={isAtTopOfPage}>
            <LayoutNavWrapper
              className="sticky p-8 lg:p-0"
              $isAtTop={isAtTopOfPage}
            >
              <Nav
                activeTopLevelUrl={parentUrl}
                $isAtTop={isAtTopOfPage}
                variant={variant}
              />
            </LayoutNavWrapper>

            <ContentContainer
              $hasBackgroundImage={hasBackgroundImage}
              $hasWhiteBackground={variant === "navy"}
            >
              {title && (
                <Section>
                  <PageHeader className="pb-10 pt-10 md:pt-24">
                    {title}
                  </PageHeader>
                </Section>
              )}
              {children}
            </ContentContainer>
            <FooterWrapper>
              <Footer />
            </FooterWrapper>
          </AppContainer>
        </AppWrapper>
      </MenuProvider>
    </ThemeProvider>
  );
};

export default Layout;
